import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { getProfile, putProfile } from "@/api/profile/index";
import { ProfileUpdateRequest } from "@/api/profile/types";
import { LINKS } from "@/constants/links";
import { TServerError } from "@/types/api/shared";
import { User } from "@/types/api/user";

import useGlobalState from "./useGlobalState";

export const PROFILE_QUERY_KEY = "profile";

const protectedRoutes = [
  LINKS.CHECKOUT,
  LINKS.RETURN,
  LINKS.ACCOUNT,
  LINKS.ACCOUNT_DELETE,
  LINKS.ACCOUNT_DELETE_SUCCESS,
  LINKS.ACCOUNT_EDIT,
  LINKS.ACCOUNT_ORDERS,
  LINKS.REGISTER_USER,
  LINKS.REGISTER_ADDRESS,
  LINKS.REGISTER_HOUSEHOLD,
];

const accessRoutes = [LINKS.LOGIN, LINKS.LOGIN_SMS];

export function useProfile({ redirectPath }: { redirectPath?: LINKS } = {}) {
  const { setRedirectPath } = useGlobalState();
  const { push, pathname } = useRouter();

  return useQuery<User, TServerError>([PROFILE_QUERY_KEY], () => getProfile(), {
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    onSuccess: () => {
      if (accessRoutes.includes(pathname as LINKS)) {
        push(LINKS.HOME);
      }
    },
    onError: () => {
      if (redirectPath) {
        setRedirectPath(redirectPath);
      }

      if (protectedRoutes.includes(pathname as LINKS)) {
        push(LINKS.LOGIN);
      }
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation<User, TServerError, ProfileUpdateRequest>({
    mutationFn: (body: ProfileUpdateRequest) => putProfile(body),
    onSuccess: (data) => {
      queryClient.setQueryData([PROFILE_QUERY_KEY], data);
    },
  });
}
